export type CrmTaskPlaybookItemKind =
  | "objective"
  | "opening"
  | "talking_point"
  | "question"
  | "objection"
  | "success_criteria"
  | "handoff"
  | "note";

export type CrmTaskPlaybookTone = "neutral" | "info" | "success" | "warning" | "danger";

export interface CrmTaskPlaybookItem {
  key: string;
  label: string;
  detail: string;
  kind: CrmTaskPlaybookItemKind;
  tone: CrmTaskPlaybookTone;
}

export interface CrmTaskPlaybook {
  key: string;
  label: string;
  summary: string | null;
  source: "configured";
  items: CrmTaskPlaybookItem[];
}

export type CrmTaskPlaybookSummaryTone = CrmTaskPlaybookTone;

export interface CrmTaskPlaybookSummary {
  playbookCount: number;
  itemCount: number;
  objectiveCount: number;
  talkingPointCount: number;
  questionCount: number;
  objectionCount: number;
  successCriteriaCount: number;
  handoffCount: number;
  warningItemCount: number;
  dangerItemCount: number;
  tone: CrmTaskPlaybookSummaryTone;
}

export interface BuildCrmTaskPlaybooksOptions {
  playbookKeys?: readonly string[];
  maxPlaybooks?: number;
  maxItemsPerPlaybook?: number;
}

export const CRM_TASK_PLAYBOOK_FIELD_KEYS = [
  "crmTaskPlaybooks",
  "taskPlaybooks",
  "operatorPlaybooks",
  "taskPlaybook",
  "callPlaybook",
  "callScript",
  "salesScript",
  "operatorScript",
  "talkTrack",
  "crmTaskTalkTrack",
] as const;

const PRIVATE_KEY_PATTERN = /(?:password|secret|token|authorization|credential|private[_-]?key|api[_-]?key)/i;
const MAX_TEXT_LENGTH = 280;

export function buildCrmTaskPlaybooks(
  taskRecords: unknown,
  options: BuildCrmTaskPlaybooksOptions = {},
): CrmTaskPlaybook[] {
  const records = toRecords(taskRecords);
  if (records.length === 0) return [];

  const playbookKeys = options.playbookKeys?.length
    ? [...options.playbookKeys]
    : [...CRM_TASK_PLAYBOOK_FIELD_KEYS];
  const maxPlaybooks = normalizePositiveInteger(options.maxPlaybooks, 4);
  const maxItemsPerPlaybook = normalizePositiveInteger(options.maxItemsPerPlaybook, 12);

  return records
    .flatMap((record) => playbookKeys.flatMap((key) => parseConfiguredPlaybooks(key, record[key], maxItemsPerPlaybook)))
    .filter((playbook) => playbook.items.length > 0)
    .slice(0, maxPlaybooks);
}

export function summarizeCrmTaskPlaybooks(
  playbooks: readonly CrmTaskPlaybook[],
): CrmTaskPlaybookSummary | null {
  const items = playbooks.flatMap((playbook) => playbook.items);
  if (items.length === 0) return null;

  const objectiveCount = items.filter((item) => item.kind === "objective").length;
  const talkingPointCount = items.filter((item) => item.kind === "talking_point").length;
  const questionCount = items.filter((item) => item.kind === "question").length;
  const objectionCount = items.filter((item) => item.kind === "objection").length;
  const successCriteriaCount = items.filter((item) => item.kind === "success_criteria").length;
  const handoffCount = items.filter((item) => item.kind === "handoff").length;
  const warningItemCount = items.filter((item) => item.tone === "warning").length;
  const dangerItemCount = items.filter((item) => item.tone === "danger").length;
  const tone = dangerItemCount > 0
    ? "danger"
    : objectionCount > 0 || warningItemCount > 0
      ? "warning"
      : successCriteriaCount > 0
        ? "success"
        : "info";

  return {
    playbookCount: playbooks.length,
    itemCount: items.length,
    objectiveCount,
    talkingPointCount,
    questionCount,
    objectionCount,
    successCriteriaCount,
    handoffCount,
    warningItemCount,
    dangerItemCount,
    tone,
  };
}

function parseConfiguredPlaybooks(
  rootKey: string,
  value: unknown,
  maxItemsPerPlaybook: number,
): CrmTaskPlaybook[] {
  if (Array.isArray(value)) {
    return value
      .map((entry, index) => parsePlaybook(`${rootKey}.${index}`, entry, maxItemsPerPlaybook))
      .filter((playbook): playbook is CrmTaskPlaybook => Boolean(playbook));
  }

  if (typeof value === "string") {
    const playbook = parseStringPlaybook(rootKey, value);
    return playbook ? [playbook] : [];
  }

  const record = asRecord(value);
  if (!record) return [];

  if (hasPlaybookShape(record)) {
    const playbook = parsePlaybook(rootKey, record, maxItemsPerPlaybook);
    return playbook ? [playbook] : [];
  }

  return Object.entries(record)
    .map(([key, entry]) => parsePlaybook(key, entry, maxItemsPerPlaybook))
    .filter((playbook): playbook is CrmTaskPlaybook => Boolean(playbook));
}

function parseStringPlaybook(fallbackKey: string, value: string): CrmTaskPlaybook | null {
  if (isPrivateKey(fallbackKey)) return null;
  const detail = truncate(value.trim());
  if (!detail) return null;
  return {
    key: fallbackKey,
    label: formatLabel(fallbackKey),
    summary: null,
    source: "configured",
    items: [
      {
        key: `${fallbackKey}.script`,
        label: "Script",
        detail,
        kind: "note",
        tone: "neutral",
      },
    ],
  };
}

function parsePlaybook(
  fallbackKey: string,
  value: unknown,
  maxItemsPerPlaybook: number,
): CrmTaskPlaybook | null {
  if (typeof value === "string") return parseStringPlaybook(fallbackKey, value);

  const record = asRecord(value);
  if (!record) return null;

  const key = readString(record.key) ?? fallbackKey;
  if (isPrivateKey(key)) return null;

  const label = readString(record.label)
    ?? readString(record.title)
    ?? formatLabel(key);
  const summary = truncate(readString(record.summary) ?? readString(record.description));
  const items = [
    ...parseSinglePlaybookField(record, ["objective", "goal", "desiredOutcome"], "objective", "Objective"),
    ...parseSinglePlaybookField(record, ["opening", "opener", "openingLine"], "opening", "Opening"),
    ...parsePlaybookItemList(record.talkingPoints ?? record.points ?? record.talkTrack, "talking_point"),
    ...parsePlaybookItemList(record.questions ?? record.discoveryQuestions ?? record.qualifyingQuestions, "question"),
    ...parsePlaybookItemList(record.objectionResponses ?? record.objections ?? record.risks, "objection"),
    ...parsePlaybookItemList(record.successCriteria ?? record.doneCriteria ?? record.winCriteria, "success_criteria"),
    ...parsePlaybookItemList(record.handoffNotes ?? record.handoff ?? record.followUpGuidance, "handoff"),
    ...parsePlaybookItemList(record.notes ?? record.reminders, "note"),
  ].slice(0, maxItemsPerPlaybook);

  return {
    key,
    label,
    summary,
    source: "configured",
    items,
  };
}

function parseSinglePlaybookField(
  record: Record<string, unknown>,
  keys: string[],
  kind: CrmTaskPlaybookItemKind,
  label: string,
): CrmTaskPlaybookItem[] {
  for (const key of keys) {
    const value = record[key];
    if (value === undefined || value === null) continue;
    const item = parsePlaybookItem(key, value, kind, label);
    return item ? [item] : [];
  }
  return [];
}

function parsePlaybookItemList(value: unknown, fallbackKind: CrmTaskPlaybookItemKind): CrmTaskPlaybookItem[] {
  if (Array.isArray(value)) {
    return value
      .map((entry, index) => parsePlaybookItem(`${fallbackKind}-${index + 1}`, entry, fallbackKind))
      .filter((item): item is CrmTaskPlaybookItem => Boolean(item));
  }

  const record = asRecord(value);
  if (!record) {
    const item = parsePlaybookItem(fallbackKind, value, fallbackKind);
    return item ? [item] : [];
  }

  return Object.entries(record)
    .map(([key, entry]) => parsePlaybookItem(key, entry, fallbackKind))
    .filter((item): item is CrmTaskPlaybookItem => Boolean(item));
}

function parsePlaybookItem(
  fallbackKey: string,
  value: unknown,
  fallbackKind: CrmTaskPlaybookItemKind,
  fallbackLabel?: string,
): CrmTaskPlaybookItem | null {
  if (isPrivateKey(fallbackKey)) return null;

  const record = asRecord(value);
  const key = record ? readString(record.key) ?? fallbackKey : fallbackKey;
  if (isPrivateKey(key)) return null;

  const kind = normalizeKind(record ? readString(record.kind) ?? readString(record.type) : null, fallbackKind);
  const label = record
    ? readString(record.label)
      ?? readString(record.title)
      ?? readString(record.name)
      ?? readString(record.objection)
      ?? fallbackLabel
      ?? formatLabel(key)
    : fallbackLabel ?? formatLabel(key);
  const detail = record
    ? truncate(
      readString(record.detail)
      ?? readString(record.description)
      ?? readString(record.text)
      ?? readString(record.value)
      ?? readString(record.script)
      ?? readString(record.response)
      ?? readString(record.guidance),
    )
    : formatValue(value);
  if (!detail) return null;

  return {
    key,
    label,
    detail,
    kind,
    tone: normalizeTone(record ? readString(record.tone) ?? readString(record.status) : null),
  };
}

function hasPlaybookShape(record: Record<string, unknown>): boolean {
  return [
    "label",
    "title",
    "summary",
    "description",
    "objective",
    "goal",
    "desiredOutcome",
    "opening",
    "opener",
    "openingLine",
    "talkingPoints",
    "points",
    "talkTrack",
    "questions",
    "discoveryQuestions",
    "qualifyingQuestions",
    "objectionResponses",
    "objections",
    "successCriteria",
    "doneCriteria",
    "winCriteria",
    "handoffNotes",
    "handoff",
    "followUpGuidance",
    "notes",
    "reminders",
  ].some((key) => key in record);
}

function normalizeKind(value: string | null, fallback: CrmTaskPlaybookItemKind): CrmTaskPlaybookItemKind {
  if (
    value === "objective"
    || value === "opening"
    || value === "talking_point"
    || value === "question"
    || value === "objection"
    || value === "success_criteria"
    || value === "handoff"
    || value === "note"
  ) {
    return value;
  }
  if (value === "talkingPoint" || value === "talking-point" || value === "point") return "talking_point";
  if (value === "successCriteria" || value === "success-criteria" || value === "doneCriteria") return "success_criteria";
  if (value === "risk" || value === "objection_response" || value === "objection-response") return "objection";
  return fallback;
}

function normalizeTone(value: string | null): CrmTaskPlaybookTone {
  if (value === "success" || value === "warning" || value === "danger" || value === "info") return value;
  if (value === "blocked" || value === "failed" || value === "overdue" || value === "missing" || value === "critical") return "danger";
  if (value === "pending" || value === "required" || value === "needs_review" || value === "partial" || value === "in_progress") return "warning";
  if (value === "complete" || value === "completed" || value === "done" || value === "verified" || value === "passed") return "success";
  if (value === "optional" || value === "review" || value === "ready") return "info";
  return "neutral";
}

function formatValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return truncate(value.trim());
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return null;
}

function toRecords(value: unknown): Record<string, unknown>[] {
  if (Array.isArray(value)) {
    return value
      .map(asRecord)
      .filter((record): record is Record<string, unknown> => Boolean(record));
  }
  const record = asRecord(value);
  return record ? [record] : [];
}

function normalizePositiveInteger(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : fallback;
}

function truncate(value: string | null): string | null {
  if (!value) return null;
  return value.length > MAX_TEXT_LENGTH ? `${value.slice(0, MAX_TEXT_LENGTH - 3)}...` : value;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function isPrivateKey(key: string): boolean {
  return PRIVATE_KEY_PATTERN.test(key);
}

function formatLabel(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
